import React from "react";
import { Outlet } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import {
  OnboardingProvider,
  useProgress,
  useShowProgress,
  useCanGoBack,
  useOnboarding,
} from "@/context/OnboardingContext";
import { useTheme } from "@/hooks/useTheme";

// Progress bar component
const OnboardingProgress: React.FC = () => {
  const progress = useProgress();
  const showProgress = useShowProgress();

  if (!showProgress) {
    return null;
  }

  return (
    <div className="w-full max-w-4xl mx-auto mb-12">
      <Progress
        value={progress}
        className="h-2 [&>div]:transition-transform [&>div]:duration-700 [&>div]:ease-out"
      />
    </div>
  );
};

// Header component with back button
const OnboardingHeader: React.FC = () => {
  const canGoBack = useCanGoBack();
  const { previousStep } = useOnboarding();

  const handleGoBack = () => {
    previousStep();
  };

  return (
    <div className="mb-12">
      <div className="w-full max-w-4xl mx-auto">
        {canGoBack && (
          <Button
            variant="ghost"
            onClick={handleGoBack}
            className="p-4 rounded-full [&_svg]:size-6"
          >
            <ArrowLeft className="h-6 w-6 stroke-[3] font-bold" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Logo component - positioned under progress bar, left aligned
const OnboardingLogo: React.FC = () => {
  const { mode } = useTheme();
  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      <div>
        <img
          src={
            mode === "dark"
              ? "/static/img/Logo_Transparency_White.png"
              : "/static/img/Logo_Transparency_Black.png"
          }
          alt="Airloom"
          width={150}
          height={150}
          className=""
        />
      </div>
    </div>
  );
};

// Inner layout component that uses the context
const OnboardingLayoutInner: React.FC = () => {
  return (
    <div className="min-h-screen bg-background dark:bg-background-dark">
      {/* Main content area */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <OnboardingHeader />
          <OnboardingProgress />
          <OnboardingLogo />

          {/* Step content */}
          <div className="py-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main onboarding layout component with provider
const OnboardingLayout: React.FC = () => {
  return (
    <OnboardingProvider>
      <OnboardingLayoutInner />
    </OnboardingProvider>
  );
};

export default OnboardingLayout;
